import { Popover } from '@blueprintjs/core';
import ClassNames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * A functional component that utilizes the `Popover` from blueprintjs to display a Tooltip when its
 * child component or element is hovered or clicked. Additional props from `Popover` may also be
 * used with this component. For a complete reference of the blueprintjs component,
 * visit https://blueprintjs.com/docs/#core/components/popover
 */
function Tooltip({
  block,
  children,
  classes,
  content,
  disabled,
  fullWidth,
  interactionKind,
  light,
  small,
  title,
  contentClasses,
  ...props
}) {
  return (
    <Popover
      className={ClassNames(
        'Tooltip__trigger',
        {
          'Tooltip__trigger--block': block,
          'Tooltip__trigger--disabled': disabled,
        },
        classes
      )}
      content={
        <>
          {title && <h6 className="Tooltip__title">{title}</h6>}
          <div className={ClassNames('Tooltip__content', contentClasses)}>{content}</div>
        </>
      }
      disabled={disabled}
      interactionKind={interactionKind}
      portalClassName={ClassNames('Tooltip', {
        'Tooltip--full-width': fullWidth,
        'Tooltip--light': light,
        'Tooltip--small': small,
      })}
      transitionDuration={200}
      {...props}
    >
      {children}
    </Popover>
  );
}

Tooltip.propTypes = {
  /**
   * Set to `true` to apply `display: block` styles to the component
   */
  block: PropTypes.bool,
  /**
   * This is the item(s) that will trigger a Tooltip to appear when it is hovered
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  /**
   * A space-separated list of classes passed to the `div` wrapping the tooltip
   */
  classes: PropTypes.string,
  /**
   * The main body text of the tooltip
   */
  content: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  /**
   * A space-separated list of classes passed to the `div` wrapping the content part of the component
   */
  contentClasses: PropTypes.string,
  /**
   * When in disabled state, tooltip does not show on hover and cursor is `not-allowed`
   */
  disabled: PropTypes.bool,
  /**
   * Set to `true` to allow the Tooltip to expand to the full width of its contents (otherwise max-width is `400px`)
   */
  fullWidth: PropTypes.bool,
  /**
   * Can be either `'hover'` or `'click'`. Other blueprintjs `PopoverInteractionKind`s are not
   * currently supported.
   */
  interactionKind: PropTypes.string,
  /**
   * Set to `true` to apply light theme to the Tooltip
   */
  light: PropTypes.bool,
  /**
   * Set to `true` to apply small styles to the text in the Tooltip
   */
  small: PropTypes.bool,
  /**
   * The header text or item in the Tooltip
   */
  title: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
};

Tooltip.defaultProps = {
  block: false,
  disabled: false,
  fullWidth: false,
  interactionKind: 'hover',
  light: false,
  small: false,
  classes: undefined,
  contentClasses: undefined,
  title: undefined,
};
Tooltip.displayName = 'Tooltip';

export default Tooltip;
